import React from 'react';
import { Handle, Position } from 'reactflow';
import { Wrench, Settings, ChevronDown, ChevronUp, AlertCircle, Edit2, X, Save } from 'lucide-react';
import { memo, useMemo, useCallback } from 'react';

interface ToolNodeProps {
  id: string;
  data: {
    label: string;
    toolType?: string;
    configuration?: string;
    description?: string;
    returnDirect?: boolean;
    onPropertyChange?: (property: string, value: any) => void;
    onLabelChange?: (label: string) => void;
    selected?: boolean;
  };
  selected: boolean;
}

const TOOL_TYPES = Object.freeze({
  // Browser Tools
  BrowserbaseLoadTool: {
    label: 'Browser Load Tool',
    description: 'Interact with and extract data from web browsers',
    configFields: ['timeout', 'headers'],
    defaultConfig: { timeout: 30, headers: {} }
  },
  // Code Tools
  CodeDocsSearchTool: {
    label: 'Code Docs Search Tool',
    description: 'Search through code documentation and technical documents',
    configFields: ['index_path', 'search_type'],
    defaultConfig: { index_path: '', search_type: 'semantic' }
  },
  CodeInterpreterTool: {
    label: 'Code Interpreter Tool',
    description: 'Interpret and execute Python code',
    configFields: ['timeout', 'memory_limit'],
    defaultConfig: { timeout: 30, memory_limit: '512M' }
  },
  // File Tools
  FileReadTool: {
    label: 'File Read Tool',
    description: 'Read and extract data from files',
    configFields: ['file_path', 'encoding'],
    defaultConfig: { file_path: '', encoding: 'utf-8' }
  },
  DirectoryReadTool: {
    label: 'Directory Read Tool',
    description: 'Read and process directory structures',
    configFields: ['dir_path', 'recursive'],
    defaultConfig: { dir_path: '', recursive: true }
  },
  // Search Tools
  CSVSearchTool: {
    label: 'CSV Search Tool',
    description: 'Search within CSV files',
    configFields: ['file_path', 'delimiter'],
    defaultConfig: { file_path: '', delimiter: ',' }
  },
  DOCXSearchTool: {
    label: 'DOCX Search Tool',
    description: 'Search within DOCX documents',
    configFields: ['file_path'],
    defaultConfig: { file_path: '' }
  },
  JSONSearchTool: {
    label: 'JSON Search Tool',
    description: 'Search within JSON files',
    configFields: ['file_path', 'jq_query'],
    defaultConfig: { file_path: '', jq_query: '.' }
  },
  PDFSearchTool: {
    label: 'PDF Search Tool',
    description: 'Search within PDF documents',
    configFields: ['file_path', 'ocr_enabled'],
    defaultConfig: { file_path: '', ocr_enabled: false }
  },
  // Web Tools
  FirecrawlSearchTool: {
    label: 'Firecrawl Search Tool',
    description: 'Search webpages using Firecrawl',
    configFields: ['max_results', 'timeout'],
    defaultConfig: { max_results: 10, timeout: 30 }
  },
  FirecrawlCrawlWebsiteTool: {
    label: 'Firecrawl Website Tool',
    description: 'Crawl websites using Firecrawl',
    configFields: ['max_depth', 'timeout'],
    defaultConfig: { max_depth: 3, timeout: 60 }
  },
  FirecrawlScrapeWebsiteTool: {
    label: 'Firecrawl Scrape Tool',
    description: 'Scrape websites using Firecrawl',
    configFields: ['selectors', 'timeout'],
    defaultConfig: { selectors: [], timeout: 30 }
  },
  // GitHub Tools
  GithubSearchTool: {
    label: 'GitHub Search Tool',
    description: 'Search within GitHub repositories',
    configFields: ['repo', 'path', 'branch'],
    defaultConfig: { repo: '', path: '', branch: 'main' }
  },
  // Database Tools
  PGSearchTool: {
    label: 'PostgreSQL Search Tool',
    description: 'Search within PostgreSQL databases',
    configFields: ['connection_string', 'query'],
    defaultConfig: { connection_string: '', query: '' }
  },
  // AI Tools
  DALLETool: {
    label: 'DALL-E Tool',
    description: 'Generate images using DALL-E',
    configFields: ['model', 'size', 'quality'],
    defaultConfig: { model: 'dall-e-3', size: '1024x1024', quality: 'standard' }
  },
  VisionTool: {
    label: 'Vision Tool',
    description: 'Process and analyze images',
    configFields: ['model', 'max_tokens'],
    defaultConfig: { model: 'gpt-4-vision-preview', max_tokens: 1000 }
  },
  // YouTube Tools
  YoutubeChannelSearchTool: {
    label: 'YouTube Channel Search Tool',
    description: 'Search within YouTube channels',
    configFields: ['channel_id', 'max_results'],
    defaultConfig: { channel_id: '', max_results: 50 }
  },
  YoutubeVideoSearchTool: {
    label: 'YouTube Video Search Tool',
    description: 'Search within YouTube videos',
    configFields: ['video_id', 'language'],
    defaultConfig: { video_id: '', language: 'en' }
  }
});

const INITIAL_STATE = {
  showConfig: false,
  showHelp: false,
  isEditing: false,
  originalValues: {} as Record<string, any>,
  dirtyFields: new Set<string>()
};

export const ToolNode = memo(({ id, data, selected }: ToolNodeProps) => {
  const [state, setState] = React.useState(INITIAL_STATE);

  const selectedTool = useMemo(() => 
    TOOL_TYPES[data.toolType as keyof typeof TOOL_TYPES],
    [data.toolType]
  );

  const handleStartEdit = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    setState(prev => ({
      ...prev,
      isEditing: true,
      originalValues: {
        toolType: data.toolType || '',
        configuration: data.configuration || '',
        description: data.description || '',
        returnDirect: data.returnDirect || false
      }
    }));
  }, [data]);

  const handleSave = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    if (data.onPropertyChange) {
      Object.entries(state.originalValues).forEach(([key, value]) => {
        if (state.dirtyFields.has(key)) {
          data.onPropertyChange?.(key, value);
        }
      });
    }
    setState(prev => ({
      ...prev,
      isEditing: false,
      originalValues: {},
      dirtyFields: new Set()
    }));
  }, [state, data.onPropertyChange]);

  const handleCancel = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    setState(prev => ({
      ...prev,
      isEditing: false,
      originalValues: {},
      dirtyFields: new Set()
    }));
  }, []);

  const handleFieldChange = useCallback((field: string, value: any) => {
    setState(prev => ({
      ...prev,
      dirtyFields: new Set([...prev.dirtyFields, field]),
      originalValues: {
        ...prev.originalValues,
        [field]: value
      }
    }));

    // Set default configuration when tool type changes
    if (field === 'toolType' && TOOL_TYPES[value as keyof typeof TOOL_TYPES]) {
      setState(prev => ({
        ...prev,
        dirtyFields: new Set([...prev.dirtyFields, 'configuration']),
        originalValues: {
          ...prev.originalValues,
          configuration: JSON.stringify(
            TOOL_TYPES[value as keyof typeof TOOL_TYPES].defaultConfig,
            null,
            2
          )
        }
      }));
    }
  }, []);

  const handleLabelChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    if (data.onLabelChange) {
      data.onLabelChange(e.target.value);
    }
  }, [data.onLabelChange]);

  const toggleConfig = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    setState(prev => ({ ...prev, showConfig: !prev.showConfig }));
  }, []);

  const baseInputClass = useMemo(() => 
    "w-full px-2 py-1 text-sm bg-gray-700 border border-gray-600 rounded text-gray-100 transition-colors",
    []
  );

  const nodeClass = useMemo(() => [
    'group relative bg-gray-800/90 backdrop-blur-sm transition-all duration-200',
    selected ? 'ring-2 ring-indigo-500 ring-inset shadow-lg shadow-indigo-500/20' : 'ring-1 ring-gray-700/50 ring-inset'
  ].join(' '), [selected]);

  return (
    <div 
      className={nodeClass}
      style={{ cursor: state.isEditing ? 'default' : 'move' }}
    >
      <Handle 
        type="target" 
        position={Position.Top} 
        className="w-2 h-2 !bg-purple-500 -top-1 border-none" 
      />

      {/* Header */}
      <div className="flex items-center justify-between p-3 border-b border-gray-700/50">
        <div className="flex items-center gap-2 flex-1 min-w-0">
          <Wrench className="w-5 h-5 text-purple-500 flex-shrink-0" />
          <input
            className="flex-1 min-w-0 bg-transparent border-none font-medium text-gray-100 focus:ring-0 focus:outline-none placeholder-gray-500"
            value={data.label || ''}
            onChange={handleLabelChange}
            placeholder="Tool Name"
            onClick={(e) => e.stopPropagation()}
          />
        </div>
        {!state.isEditing ? (
          <button
            onClick={handleStartEdit}
            className="p-1 -m-1 hover:bg-gray-700/50 rounded-full text-gray-400 hover:text-gray-300 opacity-0 group-hover:opacity-100 transition-all"
          >
            <Edit2 className="w-4 h-4" />
          </button>
        ) : (
          <div className="flex items-center gap-1">
            <button
              onClick={handleCancel}
              className="p-1 -m-1 hover:bg-gray-700/50 rounded-full text-gray-400 hover:text-gray-300"
            >
              <X className="w-4 h-4" />
            </button>
            <button
              onClick={handleSave}
              className="p-1 -m-1 hover:bg-gray-700/50 rounded-full text-gray-400 hover:text-gray-300"
            >
              <Save className="w-4 h-4" />
            </button>
          </div>
        )}
      </div>

      {/* Tool Type Badge */}
      {!state.isEditing && selectedTool && (
        <div className="px-3 py-2 text-sm text-gray-400 border-b border-gray-700/50">
          {selectedTool.label}
        </div>
      )}

      {/* Expanded Content */}
      {state.isEditing && (
        <div className="divide-y divide-gray-700/50" onClick={(e) => e.stopPropagation()}>
          <div className="p-3 space-y-1">
            <label className="block text-xs font-medium text-gray-400">Tool Type</label>
            <select
              className={baseInputClass}
              value={state.originalValues.toolType || ''}
              onChange={(e) => handleFieldChange('toolType', e.target.value)}
            >
              <option value="">Select Tool Type</option>
              <optgroup label="Browser Tools">
                <option value="BrowserbaseLoadTool">Browser Load Tool</option>
              </optgroup>
              <optgroup label="Code Tools">
                <option value="CodeDocsSearchTool">Code Docs Search Tool</option>
                <option value="CodeInterpreterTool">Code Interpreter Tool</option>
              </optgroup>
              <optgroup label="File Tools">
                <option value="FileReadTool">File Read Tool</option>
                <option value="DirectoryReadTool">Directory Read Tool</option>
              </optgroup>
              <optgroup label="Search Tools">
                <option value="CSVSearchTool">CSV Search Tool</option>
                <option value="DOCXSearchTool">DOCX Search Tool</option>
                <option value="JSONSearchTool">JSON Search Tool</option>
                <option value="PDFSearchTool">PDF Search Tool</option>
              </optgroup>
              <optgroup label="Web Tools">
                <option value="FirecrawlSearchTool">Firecrawl Search Tool</option>
                <option value="FirecrawlCrawlWebsiteTool">Firecrawl Crawl Website Tool</option>
                <option value="FirecrawlScrapeWebsiteTool">Firecrawl Scrape Website Tool</option>
              </optgroup>
              <optgroup label="GitHub Tools">
                <option value="GithubSearchTool">GitHub Search Tool</option>
              </optgroup>
              <optgroup label="Database Tools">
                <option value="PGSearchTool">PostgreSQL Search Tool</option>
              </optgroup>
              <optgroup label="AI Tools">
                <option value="DALLETool">DALL-E Tool</option>
                <option value="VisionTool">Vision Tool</option>
              </optgroup>
              <optgroup label="YouTube Tools">
                <option value="YoutubeChannelSearchTool">YouTube Channel Search Tool</option>
                <option value="YoutubeVideoSearchTool">YouTube Video Search Tool</option>
              </optgroup>
            </select>
          </div>

          {selectedTool && (
            <div className="p-3 space-y-1">
              <label className="block text-xs font-medium text-gray-400">Configuration</label>
              <textarea
                className={`${baseInputClass} font-mono`}
                placeholder="Configuration (JSON)"
                rows={4}
                value={state.originalValues.configuration || JSON.stringify(selectedTool.defaultConfig, null, 2)}
                onChange={(e) => {
                  try {
                    JSON.parse(e.target.value); // Validate JSON
                    handleFieldChange('configuration', e.target.value);
                  } catch (error) {
                    // Allow invalid JSON while typing, but don't update the state
                  }
                }}
              />
            </div>
          )}

          <div className="p-3 space-y-1">
            <label className="block text-xs font-medium text-gray-400">Description</label>
            <input
              className={baseInputClass}
              placeholder="Description"
              value={state.originalValues.description || ''}
              onChange={(e) => handleFieldChange('description', e.target.value)}
            />
          </div>

          <div className="p-3 space-y-1">
            <label className="block text-xs font-medium text-gray-400">Return Direct</label>
            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                className="w-4 h-4 text-purple-600 border-gray-600 rounded focus:ring-purple-500 bg-gray-700 transition-colors"
                checked={state.originalValues.returnDirect || false}
                onChange={(e) => handleFieldChange('returnDirect', e.target.checked)}
              />
              <span className="text-sm text-gray-300">Return Direct</span>
            </div>
          </div>
        </div>
      )}

      <Handle 
        type="source" 
        position={Position.Bottom} 
        className="w-2 h-2 !bg-purple-500 -bottom-1 border-none" 
      />
    </div>
  );
});

ToolNode.displayName = 'ToolNode';
